import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { vendorApi } from '#services/vendor';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface VendorListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterForQuery: string;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
}

interface GetVendorsFulfilledAction {
  payload: unknown;
  meta: { arg: { originalArgs: unknown } };
}

const initialState: VendorListState = {
  filter: '',
  dateRange: buildTimeRange(ALL_TIME_RANGE_PRESET),
  filterForQuery: '',
  order: 'asc',
  orderBy: 'name',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
};

const handleGetVendorsFulfilled = (
  state: VendorListState,
  action: GetVendorsFulfilledAction,
) => {
  const {
    page,
    data,
    total,
  } = action.payload as { page: number; data?: unknown[]; total: number };
  const args = action.meta.arg.originalArgs as {
    order: string;
    orderBy: string;
    filter: string;
    page: number;
    pageSize: number;
  };

  const dimensionsMatch = (
    args.order === state.order
    && args.orderBy === state.orderBy
    && args.filter === state.filterForQuery
    && args.page === state.pageIndex
    && args.pageSize === state.pageSize
  );

  if (!dimensionsMatch) {
    return;
  }

  state.pageIndex = page;
  state.rows = data || [];
  state.rowCount = total;
};

export const vendorListSlice = createSlice({
  name: 'vendorList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterForQuery(state, action: PayloadAction<string>) {
      state.filterForQuery = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(vendorApi.endpoints.getVendors.matchFulfilled, handleGetVendorsFulfilled)
      .addMatcher(vendorApi.endpoints.deleteVendor.matchFulfilled, handleGetVendorsFulfilled);
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterForQuery,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
} = vendorListSlice.actions;

export default vendorListSlice.reducer;
